import React from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import { Control, useWatch  } from "react-hook-form";
import CustomTextField from "./CustomTextField";
import { tokens } from "../../theme";
import CloseIcon from "@mui/icons-material/Close";

interface CustomAmountRangeProps {
  label?: string;
  control: Control<any, any>;
  minControllerName: string;
  maxControllerName: string;
  minLabel?: string;
  maxLabel?: string;
  disabled?: boolean;
  outlinedModal?: boolean;
}

const CustomAmountRange: React.FC<CustomAmountRangeProps> = ({
  label,
  control,
  minControllerName,
  maxControllerName,
  minLabel = "Min",
  maxLabel = "Max",
  disabled = false,
  outlinedModal = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const minValue = useWatch({ control, name: minControllerName });
  const maxValue = useWatch({ control, name: maxControllerName });
  const hasValues = minValue || maxValue;

  const handleClear = () => {
    control._reset({
      ...control._formValues,
      [minControllerName]: "",
      [maxControllerName]: "",
    });
  };

  if (outlinedModal) {
    return (
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1, pr: hasValues && !disabled ? 4 : 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextField
            control={control}
            controllerName={minControllerName}
            name={minControllerName}
            label={minLabel}
            isNumberOnly={true}
            isDisabled={disabled}
            size="small"
            variant="outlined"
          />
        </Box>
        <Box
          sx={{
            color: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[500],
            fontSize: "14px",
            flexShrink: 0,
          }}
        >
          -
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextField
            control={control}
            controllerName={maxControllerName}
            name={maxControllerName}
            label={maxLabel}
            isNumberOnly={true}
            isDisabled={disabled}
            size="small"
            variant="outlined"
          />
        </Box>
        {hasValues && !disabled && (
          <IconButton
            onClick={handleClear}
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              padding: "4px",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[100],
              },
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.grey[700] : colors.primary[100]
        }`,
        borderRadius: "4px",
        padding: "5px 8px",
        paddingRight: hasValues ? "32px" : "8px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        backgroundColor: "transparent",
        "&:hover": {
          borderColor: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500],
        },
      }}
    >
      {label && (
        <Box
          component="label"
          sx={{
            position: "absolute",
            top: 0,
            left: "8px",
            transform: "translateY(-50%)",
            backgroundColor: theme.palette.mode === "dark" ? "#171e26" : "#f9f6f6",
            padding: "0 4px",
            fontSize: "11px",
            color: theme.palette.mode === "dark" ? colors.grey[400] : colors.primary[100],
          }}
        >
          {label}
        </Box>
      )}
      {hasValues && !disabled && (
        <IconButton
          onClick={handleClear}
          sx={{
            position: "absolute",
            top: "50%",
            right: "4px",
            transform: "translateY(-50%)",
            padding: "4px",
            backgroundColor: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[700],
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[600],
            },
          }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      )}
      <Box sx={{ flex: 1 }}>
        <CustomTextField
          control={control}
          controllerName={minControllerName}
          name={minControllerName}
          label={minLabel}
          isNumberOnly={true}
          isDisabled={disabled}
          size="small"
          variant="standard"
        />
      </Box>
      <Box
        sx={{
          color: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[500],
          fontSize: "14px",
        }}
      >
        -
      </Box>
      <Box sx={{ flex: 1 }}>
        <CustomTextField
          control={control}
          controllerName={maxControllerName}
          name={maxControllerName}
          label={maxLabel}
          isNumberOnly={true}
          isDisabled={disabled}
          size="small"
          variant="standard"
        />
      </Box>
    </Box>
  );
};

export default CustomAmountRange;
